import CheckCircle from '@mui/icons-material/CheckCircle'
import {
    Button,
    Dialog,
    DialogContent,
    Grid,
    TextField,
    Typography,
} from '@mui/material'
import AppDialogTitle from 'components/Dialog/AppDialogTitle'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormat } from 'Services/localize/format'
import { EMAIL_REGEXP } from 'utilities/stringHelper'
import { TopUpResult } from './types'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import useTopUpReceipt from './useTopUpReceipt'
import PrintReceiptPreviewModal from 'components/PrintReceiptPreview/PrintReceiptPreviewModal'
import { convertSvgImageToPng } from 'utilities/svgConverter'
import { PreviewImg, receiptButton } from '../styles/receiptDialog'
import { logHWError } from 'utilities/logError'
import CloseDialogButton from 'components/Dialog/CloseDialogButton'

interface Props {
    data: TopUpResult
    onClose: () => void
}

function TopUpReceiptDialog({ data, onClose }: Props) {
    const { t } = useTranslation()
    const { currency } = useFormat()

    const [sendingEmail, setSendingEmail] = useState(false)
    const [customerEmail, setCustomerEmail] = useState('')
    const [openReceiptPreview, setOpenReceiptPreview] = useState(false)
    const imgRef = useRef<HTMLImageElement>(null)

    const {
        isPrinting,
        isReceiptReady,
        previewSvg,
        enableOnScreenPrinter,
        parseReceiptTemplate,
        sendReceiptToEmail,
        onPrintReceipt,
    } = useTopUpReceipt(data.giftCardSaleID)

    const handleEmailReceipt = useCallback(
        (event: any) => {
            if (!customerEmail) return

            setSendingEmail(true)

            event.preventDefault()
            parseReceiptTemplate()

            setTimeout(async () => {
                if (imgRef.current) {
                    const png = convertSvgImageToPng(imgRef.current)
                    if (png) await sendReceiptToEmail(customerEmail, png)
                    setSendingEmail(false)
                }
            }, 1000)
        },
        [customerEmail, parseReceiptTemplate, sendReceiptToEmail],
    )
    const handlePrintSaleReceipt = useCallback(async () => {
        if (enableOnScreenPrinter) {
            parseReceiptTemplate()
            setOpenReceiptPreview(true)
        } else {
            await onPrintReceipt().catch((err) =>
                logHWError('TopUpReceiptDialog', 'print', err),
            )
        }
    }, [enableOnScreenPrinter, onPrintReceipt, parseReceiptTemplate])

    useEffect(() => {
        if (data.customerEmail) setCustomerEmail(data.customerEmail)
    }, [data.customerEmail])

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            onClose={() => {}}
            disableEscapeKeyDown
        >
            <AppDialogTitle
                text={t(
                    'quick_sell.gift_card_top_up.label.receipt_dialog_title',
                )}
            />
            <CloseDialogButton
                dialogName="top up receipt dialog"
                onClick={onClose}
            />
            <DialogContent style={{ padding: 5 }}>
                <Grid
                    container
                    direction="column"
                    sx={{ flexGrow: 1, padding: 1 }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid item>
                        <CheckCircle style={{ fontSize: 70 }} color="primary" />
                    </Grid>

                    <Grid item>
                        <Typography variant="h3">
                            {t(
                                'quick_sell.receipt_dialog.label.payment_complete',
                            )}
                        </Typography>
                    </Grid>

                    {data.changeAmount > 0 && (
                        <Grid item>
                            <Typography
                                variant="h3"
                                style={{ fontWeight: 600 }}
                            >
                                {t('quick_sell.receipt_dialog.label.change')}:{' '}
                                {currency(data.changeAmount)}
                            </Typography>
                        </Grid>
                    )}

                    <Grid item>
                        <Typography variant="h3" style={{ fontWeight: 600 }}>
                            {t(
                                'quick_sell.gift_card_top_up.label.current_balance',
                            )}
                            : {currency(data.currentBalance)}
                        </Typography>
                    </Grid>

                    <Grid item container justifyContent="center" spacing={1}>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                placeholder="customer@email.com"
                                value={customerEmail}
                                onChange={(e) => {
                                    setCustomerEmail(e.target.value)
                                }}
                                disabled={
                                    isPrinting ||
                                    sendingEmail ||
                                    !isReceiptReady
                                }
                                size="medium"
                            ></TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Button
                                size="large"
                                color="primary"
                                disabled={
                                    isPrinting ||
                                    sendingEmail ||
                                    !isReceiptReady ||
                                    !EMAIL_REGEXP.test(customerEmail)
                                }
                                fullWidth
                                sx={receiptButton}
                                onClick={handleEmailReceipt}
                            >
                                {t(
                                    'quick_sell.gift_card_top_up.label.email_receipt',
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid item container justifyContent={'space-between'}>
                        <Grid item xs="auto">
                            <Button
                                disabled={
                                    isPrinting ||
                                    sendingEmail ||
                                    !isReceiptReady
                                }
                                onClick={handlePrintSaleReceipt}
                                color="primary"
                                startIcon={<ReceiptLongIcon />}
                                size="large"
                                sx={{
                                    ...receiptButton,
                                    ml: 1,
                                }}
                            >
                                {t(
                                    'quick_sell.receipt_dialog.label.print_receipt',
                                )}
                            </Button>
                        </Grid>
                        <Grid item xs="auto">
                            <Button
                                onClick={onClose}
                                sx={receiptButton}
                                color="primary"
                                size="large"
                            >
                                {t('quick_sell.receipt_dialog.label.done')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <PreviewImg
                    src={previewSvg}
                    alt="receipt-preview"
                    ref={imgRef}
                />
            </DialogContent>
            <PrintReceiptPreviewModal
                title={t('receipt_dialog.label.preview_receipt')}
                open={openReceiptPreview}
                onClose={() => setOpenReceiptPreview(false)}
                previewMarkup={previewSvg}
            />
        </Dialog>
    )
}

export default TopUpReceiptDialog
